"use client";

import slugify from "@sindresorhus/slugify";
import { useTranslations } from "next-intl";
import Image from "next/image";
import * as React from "react";
import { z } from "zod";

import { Header } from "@/app/(site)/[lang]/_layout";
import { IconMenu16Line, IconXmark16Line } from "@/components/icons/iconoir";
import { SelectIcon } from "@/components/icons/select-icon";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { imageSchema } from "@/schemas";

import { AnimatePresence, LazyMotion } from "../animate";
import { BrandAssetsContextMenu } from "../brand-assets/brand-assets-context-menu";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { buttonVariants } from "../ui/button-variants";
import { CtaLink } from "../ui/cta-link";
import { Link } from "../ui/link";
import { MobileNav } from "./mobile-nav";
import { NavLink } from "./nav-link";
import { resolveNavMegaMenu } from "./nav-mega-menu-data";
import { NavigationDropdown } from "./navigation-dropdown";
import { TrustpilotRating } from "./trustpilot-rating";
import {
  bannerSchema,
  navCardLinkListSchema,
  navListV2Schema,
  navTextLinkListSchema,
  navV2Schema,
} from "./types";
import { useColorBlockOverlap } from "./use-color-block-overlap";
import { useHeroOverlayNav } from "./use-hero-overlay-nav";
import { isMenuPathActive } from "./utils";

export function MainNav({
  image,
  banner,
  isDark = false,
  overlayHero = false,
  minimal = false,
  showSignIn = false,
  locale,
  ...props
}: z.infer<typeof navV2Schema> & {
  image?: z.infer<typeof imageSchema>;
  banner: z.infer<typeof bannerSchema> | null;
  isDark?: boolean;
  locale?: string;
  /**
   * Transparent nav over a full-bleed hero at the top of the page; snaps to
   * the standard light bar once the hero scrolls away (visionaries-summit pattern).
   */
  overlayHero?: boolean;
  /**
   * Stripped-back nav for focused landing pages (e.g. flexible-funding):
   * logo + Trustpilot rating + primary CTA only — no menu, no "Sign in".
   */
  minimal?: boolean;
  /** Keep the "Sign in" link in the minimal nav (for existing customers). */
  showSignIn?: boolean;
}) {
  const t = useTranslations("Nav");
  const { ctas, menu } = props;
  const [showMobileMenu, setShowMobileMenu] = React.useState<boolean>(false);
  const [isDesktopNavOpen, setIsDesktopNavOpen] =
    React.useState<boolean>(false);
  const [ref, isColored] = useColorBlockOverlap();
  const isAnyNavOpen = isDesktopNavOpen || showMobileMenu;
  const { isOverHero, isAtPageTop } = useHeroOverlayNav(overlayHero);
  // Transparent only at absolute page top while the hero still sits under the nav.
  const isOverlayAtTop = overlayHero && isOverHero && isAtPageTop;
  // Dark nav chrome: always for isDark pages; on overlayHero pages once scrolled past hero band.
  const isDarkNavChrome = isDark || (overlayHero && isOverHero && !isAtPageTop);
  const isLightNav = isDark || (overlayHero && isOverHero);
  // Mobile drawer always uses the light bar + dark logo/icons while open.
  const isMobileLightText = !showMobileMenu && isLightNav;

  // Close the mobile drawer when crossing the lg breakpoint so body lock,
  // header theme, and nav state reset for desktop layout.
  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1056px)");
    const closeOnDesktop = () => {
      if (mediaQuery.matches) {
        setShowMobileMenu(false);
      }
    };

    closeOnDesktop();
    mediaQuery.addEventListener("change", closeOnDesktop);
    return () => mediaQuery.removeEventListener("change", closeOnDesktop);
  }, []);

  return (
    <Header
      ref={ref}
      isColored={isColored}
      isNavOpen={isAnyNavOpen}
      isDark={isDarkNavChrome}
      isMobileNavOpen={showMobileMenu}
      overlayAtTop={isOverlayAtTop}
      banner={banner}
    >
      <div className="col-span-full flex justify-between">
        <div className="flex gap-x-6">
          <NavLogo
            isLight={isLightNav}
            className={cn("lg:flex lg:items-center")}
            image={image}
            t={t}
          />
          {!minimal && menu && (
            <nav aria-label="Main navigation" className="hidden lg:flex">
              <NavMenu
                className="text-body-sm"
                menu={menu}
                onNavOpen={setIsDesktopNavOpen}
                isNavOpen={isAnyNavOpen}
                isDark={isLightNav}
                locale={locale}
              />
            </nav>
          )}
        </div>

        {ctas && (
          <div className="hidden lg:flex">
            <NavCtas
              isDark={isLightNav}
              isOverlayAtTop={isOverlayAtTop}
              minimal={minimal}
              showSignIn={showSignIn}
              {...ctas}
              t={t}
            />
          </div>
        )}
      </div>

      <div className="col-span-full flex w-full items-center justify-between lg:hidden">
        <div className={cn("flex items-center")}>
          <Link
            href="/"
            className="flex justify-center"
            aria-label={t("goToHomepage")}
            onClick={() => setShowMobileMenu(false)}
          >
            <BrandAssetsContextMenu>
              <WayflyerLogo
                light={showMobileMenu ? false : isMobileLightText}
                aria-label={t("wayflyerLogo")}
                role="img"
              />
            </BrandAssetsContextMenu>
          </Link>
        </div>
        {minimal
          ? ctas?.primaryCta && (
              <div className="flex items-center gap-x-3 place-self-end self-center">
                <TrustpilotRating
                  className="hidden sm:flex"
                  inverted={isMobileLightText}
                />
                {showSignIn && (
                  <NavSignInLink
                    secondaryCta={ctas.secondaryCta}
                    isDark={isMobileLightText}
                    t={t}
                  />
                )}
                <NavPrimaryCta
                  primaryCta={ctas.primaryCta}
                  isDark={isMobileLightText}
                />
              </div>
            )
          : (menu || ctas) && (
              <div className="flex gap-x-2 place-self-end self-center">
                {ctas?.primaryCta && (
                  <NavPrimaryCta
                    primaryCta={ctas.primaryCta}
                    isDark={isMobileLightText}
                  />
                )}
                <button
                  aria-haspopup
                  aria-expanded={showMobileMenu}
                  onClick={() => setShowMobileMenu((v) => !v)}
                >
                  {showMobileMenu ? (
                    <figure className="border-grey-100 bg-grey-0 flex size-8 items-center justify-center rounded-full border">
                      <IconXmark16Line className="text-grey-950 size-3" />
                    </figure>
                  ) : (
                    <figure
                      className={cn(
                        "flex size-8 items-center justify-center rounded-full border",
                        // Figma menu icon (3583:23544): white circle, grey-100 border.
                        isMobileLightText
                          ? "border-white/40 bg-white/10"
                          : "border-grey-100 bg-grey-0"
                      )}
                    >
                      <IconMenu16Line
                        className={cn(
                          "size-3",
                          isMobileLightText ? "text-grey-0" : "text-grey-950"
                        )}
                      />
                    </figure>
                  )}
                  <span className="sr-only">{t("menu")}</span>
                </button>
              </div>
            )}
      </div>
      {/* Mobile navigation */}
      <LazyMotion>
        <AnimatePresence>
          {showMobileMenu && (
            <MobileNav
              key="mobile-nav"
              {...props}
              isDark={isLightNav}
              locale={locale}
              onItemPress={() => setShowMobileMenu(false)}
            />
          )}
        </AnimatePresence>
      </LazyMotion>
    </Header>
  );
}

function NavLogo({
  image,
  className,
  isLight,
  t,
}: {
  image?: z.infer<typeof imageSchema>;
  className?: string;
  isLight: boolean;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <div className={cn(className)}>
      <Link
        href="/"
        className={cn("hidden flex-none items-center lg:flex", {
          "pr-2": image,
          "col-span-full": !image,
        })}
        aria-label={t("goToHomepage")}
      >
        <BrandAssetsContextMenu>
          <WayflyerLogo
            light={isLight}
            aria-label={t("wayflyerLogo")}
            role="img"
          />
        </BrandAssetsContextMenu>
      </Link>

      {image && (
        <div className="hidden items-center lg:flex">
          <Separator
            className="bg-surface-inverse mx-2 h-10 w-px"
            orientation="vertical"
          />
          <figure>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            {image?.src && (
              <img
                className="h-[28px] w-auto"
                src={image.src}
                alt={image.alt ?? ""}
              />
            )}
          </figure>
        </div>
      )}
    </div>
  );
}

function NavMenu({
  menu,
  className,
  onNavOpen,
  isNavOpen,
  isDark = false,
  locale,
}: {
  className?: string;
  menu: NonNullable<z.infer<typeof navV2Schema>["menu"]>;
  onNavOpen: (v: boolean) => void;
  isNavOpen: boolean;
  isDark?: boolean;
  locale?: string;
}) {
  const pathname = usePathname();
  const isHome = pathname === "/";
  const darkLinkClass =
    isDark &&
    "text-grey-300 hover:bg-transparent hover:text-grey-300 data-popup-open:text-grey-0";
  return (
    <NavigationMenu className={cn(className)} onOpenChange={onNavOpen}>
      <NavigationMenuList className="lg:flex">
        {menu.map((menuItem) => {
          if (menuItem._type === "navTextLink") {
            return (
              <NavigationMenuItem key={menuItem.title}>
                <NavigationMenuLink
                  render={
                    <Link
                      suppressHydrationWarning
                      href={menuItem.url}
                      external={menuItem.external}
                    />
                  }
                  className={cn(
                    navigationMenuTriggerStyle,
                    darkLinkClass,
                    !isNavOpen &&
                      !isHome &&
                      menuItem.url.includes(pathname) &&
                      (isDark ? "text-grey-0" : "text-black")
                  )}
                >
                  {menuItem.title}
                </NavigationMenuLink>
              </NavigationMenuItem>
            );
          }

          return (
            <NavigationMenuItem key={menuItem.title}>
              <NavigationMenuTrigger
                suppressHydrationWarning
                className={cn(
                  darkLinkClass,
                  !isNavOpen &&
                    !isHome &&
                    isMenuPathActive(menuItem, pathname) &&
                    (isDark ? "text-grey-0" : "text-black")
                )}
              >
                {menuItem.title}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                {(() => {
                  const megaMenu = resolveNavMegaMenu(menuItem.title, locale);
                  if (megaMenu) {
                    return <NavigationDropdown menu={megaMenu} />;
                  }
                  return <LegacyNavigationDropdown menuItem={menuItem} />;
                })()}
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
}

function LegacyNavigationDropdown({
  menuItem,
}: {
  menuItem: z.infer<typeof navListV2Schema>;
}) {
  return (
    <div
      className={cn(
        "grid sm:gap-0",
        menuItem.featuredContentV2?.items && "grid-cols-12",
        !menuItem.featuredContentV2?.items &&
          menuItem.subMenu &&
          menuItem.subMenu.length === 1 &&
          "grid-cols-4",
        !menuItem.featuredContentV2?.items &&
          menuItem.subMenu &&
          menuItem.subMenu.length === 2 &&
          "grid-cols-6",
        !menuItem.featuredContentV2?.items &&
          menuItem.subMenu &&
          menuItem.subMenu.length >= 3 &&
          "grid-cols-12"
      )}
    >
      <div
        className={cn(
          "grid rounded-xl bg-white [box-shadow:2px_23px_40px_10px_rgba(0,0,0,0.05)]",
          menuItem.featuredContentV2?.items && "col-span-8",
          menuItem.featuredContentV2?.items &&
            menuItem.subMenu &&
            menuItem.subMenu.length >= 3 &&
            "col-span-9",
          !menuItem.featuredContentV2?.items && "col-span-full",
          menuItem.subMenu && menuItem.subMenu.length === 2 && "grid-cols-2",
          menuItem.subMenu && menuItem.subMenu.length >= 3 && "grid-cols-3"
        )}
      >
        {menuItem.subMenu?.map((subMenuItem, subMenuIndex) => (
          <NavigationContentGroup
            key={subMenuItem._id ?? String(subMenuIndex)}
            className={cn("min-w-[338px] p-5")}
          >
            <NavigationContentList
              className={cn(
                "grid gap-5",
                menuItem.subMenu?.length === 1 && "grid-cols-2"
              )}
            >
              {subMenuItem.links.map((navLinkItem) =>
                navLinkItem._type === "navCardLink" ? (
                  <NavigationMenuLink
                    key={navLinkItem.url}
                    render={
                      <Link
                        href={navLinkItem.url}
                        external={navLinkItem.external}
                      />
                    }
                  >
                    <NavCardItem {...navLinkItem} className="size-full" />
                  </NavigationMenuLink>
                ) : (
                  <ListItem key={navLinkItem.url} {...navLinkItem} />
                )
              )}
            </NavigationContentList>
          </NavigationContentGroup>
        ))}
      </div>
      {menuItem.featuredContentV2?.items && (
        <FeaturedContent
          className={cn(
            "col-span-4 px-5 py-3",
            menuItem.featuredContentV2?.items &&
              menuItem.subMenu &&
              menuItem.subMenu.length >= 3 &&
              "col-span-3"
          )}
          {...menuItem.featuredContentV2}
        />
      )}
    </div>
  );
}

function FeaturedContent({
  className,
  items,
  viewAllLink,
}: z.infer<typeof navListV2Schema>["featuredContentV2"] & {
  className?: string;
}) {
  if (items?.length === 1) {
    const item = items[0];
    if (!item) return null;
    return (
      <div className={cn(className, "flex flex-col gap-4")}>
        <figure className="size-16">
          {item.image?.src && (
            <Image
              src={item.image.src}
              alt={item.image.alt ?? ""}
              width={item.image.width ?? 0}
              height={item.image.height ?? 0}
              className="rounded-container size-full object-cover"
            />
          )}
        </figure>
        <div className="col-span-2 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <p className="text-body-2xs text-grey-400 font-semibold uppercase">
              {item.shortDescription}
            </p>
            <p className="text-body text-grey-900">{item.title}</p>
          </div>
          <div>
            <CtaLink
              className="text-body p-0"
              href={item.linkUrl}
              variant="link"
            >
              {item.linkText}{" "}
            </CtaLink>
          </div>
        </div>
      </div>
    );
  }

  if ((items?.length ?? 0) > 1) {
    return (
      <div className={cn(className, "flex flex-col justify-between gap-4")}>
        <ul className="flex flex-col gap-4">
          {items?.map((item) => (
            <li className="group" key={item.linkUrl}>
              <Link className="flex items-center gap-4" href={item.linkUrl}>
                <figure className="size-16">
                  {item.image?.src && (
                    <Image
                      src={item.image.src}
                      alt={item.image.alt ?? ""}
                      width={item.image.width ?? 0}
                      height={item.image.height ?? 0}
                      className="rounded-container size-full object-cover"
                    />
                  )}
                </figure>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <p className="text-body-2xs text-grey-400 font-semibold uppercase">
                      {item.shortDescription}
                    </p>
                    <p className="text-body text-grey-900 group-hover:underline">
                      {item.title}
                    </p>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
        {viewAllLink && (
          <CtaLink
            className="text-body justify-start p-0"
            href={viewAllLink.linkUrl}
            variant="link"
          >
            {viewAllLink.linkText}
          </CtaLink>
        )}
      </div>
    );
  }

  return null;
}

type NavPrimaryCtaData = NonNullable<
  NonNullable<z.infer<typeof navV2Schema>["ctas"]>["primaryCta"]
>;

function NavPrimaryCta({
  primaryCta,
  isDark,
}: {
  primaryCta: NavPrimaryCtaData;
  isDark: boolean;
}) {
  return (
    <Button
      variant={isDark ? "secondary-gradient-hover" : "tertiary"}
      gradientSurface={isDark ? "dark" : undefined}
      size="default"
      render={
        <Link
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Base UI otherwise assigns role="button" to the rendered anchor.
          role="link"
          external={primaryCta.external ?? false}
          href={primaryCta.url}
          data-ph-capture-attribute-id={`main-nav-primary-cta-${slugify(primaryCta.label)}`}
          data-ph-capture-attribute-cta_label={primaryCta.label}
          data-ph-capture-attribute-cta_href={primaryCta.url}
          data-ph-capture-attribute-cta_url={primaryCta.url}
        />
      }
    >
      {primaryCta.label}
    </Button>
  );
}

function NavCtas({
  primaryCta,
  secondaryCta,
  tertiaryCta,
  className,
  isDark,
  isOverlayAtTop = false,
  minimal,
  showSignIn,
  t,
}: NonNullable<z.infer<typeof navV2Schema>["ctas"]> & {
  className?: string;
  isDark: boolean;
  isOverlayAtTop?: boolean;
  minimal?: boolean;
  showSignIn?: boolean;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <div className={cn("flex items-center gap-x-3", className)}>
      {minimal ? (
        <TrustpilotRating className="mr-4" inverted={isDark} />
      ) : (
        <Link
          href="/book-a-call"
          className={cn(
            // Figma nav CTA — body/sm (nav-item size): Inter 14px / 500, grey-950.
            "text-ds-body-sm inline-flex items-center px-4 py-1.5 font-medium transition-colors",
            isOverlayAtTop
              ? "text-grey-300 hover:text-grey-0"
              : isDark
                ? "text-grey-300 hover:text-grey-0"
                : "text-grey-700 hover:text-grey-950"
          )}
          data-ph-capture-attribute-id="main-nav-tertiary-cta-book-a-call"
          data-ph-capture-attribute-cta_label={tertiaryCta ?? t("talkToSales")}
          data-ph-capture-attribute-cta_href="/book-a-call"
        >
          {tertiaryCta ?? t("talkToSales")}
        </Link>
      )}
      {(!minimal || showSignIn) && (
        <NavSignInLink secondaryCta={secondaryCta} isDark={isDark} t={t} />
      )}
      {primaryCta && <NavPrimaryCta primaryCta={primaryCta} isDark={isDark} />}
    </div>
  );
}

function NavSignInLink({
  secondaryCta,
  isDark,
  t,
}: {
  secondaryCta?: { label: string; url: string; external?: boolean } | null;
  isDark: boolean;
  t: ReturnType<typeof useTranslations>;
}) {
  const href = secondaryCta?.url ?? "https://app.wayflyer.com/authn/login";
  const label = secondaryCta?.label ?? t("signIn");
  return (
    <Link
      className={cn(
        buttonVariants({
          variant: "secondary",
          size: "default",
        }),
        "px-4",
        isDark &&
          "text-grey-0 border-white/30 bg-white/10 hover:bg-white/15 active:bg-white/20"
      )}
      href={href}
      data-ph-capture-attribute-id="main-nav-cta-login"
      data-ph-capture-attribute-cta_label={label}
      data-ph-capture-attribute-cta_href={href}
      external={secondaryCta?.external ?? false}
    >
      {label}
    </Link>
  );
}

function NavCardItem({
  className,
  ...props
}: z.infer<typeof navCardLinkListSchema>["links"][0] & { className?: string }) {
  return (
    <div className={cn("group flex items-start gap-2", className)}>
      <div className={cn("flex items-center gap-3")}>
        <div className="bg-grey-50 flex aspect-square size-12 items-center justify-center overflow-hidden rounded-full">
          {props.variant === "image" && props.image?.src && (
            <Image
              src={props.image.src}
              alt={props.image.alt ?? ""}
              width={props.image.width ?? 0}
              height={props.image.height ?? 0}
              className="object-cover"
            />
          )}
          {props.variant === "icon" && props.icon && (
            <SelectIcon id={props.icon} className="size-6" alt={props.icon} />
          )}
        </div>
        <div>
          <div className="text-heading-16 xl:text-heading-20 group-hover:underline">
            {props.title}
          </div>
          {props.label && (
            <div className="text-body-sm text-grey-400 normal-case">
              {props.label}
            </div>
          )}
        </div>
      </div>
      {props.tag && <Badge className="uppercase">{props.tag}</Badge>}
    </div>
  );
}

function ListItem({
  title,
  url,
  external,
}: z.infer<typeof navTextLinkListSchema>["links"][0]) {
  return (
    <li>
      <NavigationMenuLink
        render={
          <NavLink
            className="lg:text-heading-20"
            title={title}
            href={url}
            external={external}
          />
        }
      />
    </li>
  );
}

function NavigationContentList({
  className,
  ...props
}: React.ComponentProps<"ul">) {
  return <ul className={cn("flex flex-col", className)} {...props} />;
}

function NavigationContentGroup({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div className={cn(className)} {...props} />;
}

export function WayflyerLogo({
  light,
  className,
  ...props
}: React.ComponentProps<"svg"> & { light?: boolean }) {
  return (
    <svg
      viewBox="0 0 147 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        "h-6 w-[110px] shrink-0 md:h-[28px] md:w-[129px]",
        className
      )}
      {...props}
    >
      <path
        d="M145.492 6.91591C144.205 6.91591 143.052 7.22723 142.032 7.82765C141.012 8.45031 140.258 9.36205 139.792 10.6074H138.55V7.11605H133.582V11.9861H135.999V24.9062H140.812V15.9222C140.812 14.5879 141.167 13.5872 141.877 12.9646C142.586 12.3197 143.584 11.9861 144.871 11.9861H147V6.91591H145.492Z"
        fill={light ? "#FFFFFF" : "#000000"}
      />
      <path
        d="M127.927 7.82766C126.596 7.11606 125.021 6.76025 123.158 6.76025C121.295 6.76025 119.61 7.13829 118.212 7.89437C116.837 8.62822 115.773 9.67339 115.019 11.0744C114.265 12.4309 113.887 14.0764 113.887 15.9666C113.887 17.8568 114.265 19.5247 115.041 20.9256C115.817 22.3044 116.904 23.394 118.323 24.1501C119.743 24.9062 121.406 25.2842 123.313 25.2842C125.221 25.2842 127.594 24.7505 129.102 23.6831C130.61 22.5935 131.586 21.1035 131.963 19.2133H127.062C126.795 19.9472 126.374 20.4809 125.753 20.8367C125.132 21.1925 124.311 21.3704 123.269 21.3704C122.227 21.3704 120.874 21.0146 120.075 20.3252C119.277 19.6359 118.767 18.6352 118.567 17.3009H132.03V15.6331C132.03 13.7874 131.675 12.2085 130.965 10.8965C130.255 9.5622 129.235 8.56151 127.882 7.8499L127.927 7.82766ZM118.745 14.032C119.011 12.9201 119.499 12.0751 120.253 11.5191C121.029 10.9409 122.005 10.6518 123.203 10.6518C124.4 10.6518 125.354 10.9409 126.064 11.5191C126.773 12.0751 127.217 12.9201 127.372 14.032H118.745Z"
        fill={light ? "#FFFFFF" : "#000000"}
      />
      <path
        d="M76.8268 0.978457C76.1171 1.60111 75.7623 2.60181 75.7623 3.93607V7.11605H72.5464V10.9632H75.7179V24.9062H80.6194V10.9632H84.545V7.11605H80.5529V3.84712H84.8999V0H79.821C78.5346 0 77.5366 0.333565 76.8268 0.978457Z"
        fill={light ? "#FFFFFF" : "#000000"}
      />
      <path
        d="M87.1176 0V20.9701C87.1176 22.3044 87.4724 23.3051 88.1822 23.9277C88.8919 24.5726 89.8899 24.9062 91.1763 24.9062H94.1926V21.0591H92.0412V0H87.1176Z"
        fill={light ? "#FFFFFF" : "#000000"}
      />
      <path
        d="M61.4128 19.1022H61.3019L56.3338 7.11606H51.1218L58.7735 24.7505L57.3762 28.0639H53.4728V32H57.5315C58.8622 32 59.8602 31.6664 60.5256 31.0215C61.1688 30.3989 61.7454 29.4871 62.2334 28.3308L63.675 24.8839L71.2823 7.09382H66.2477L61.3906 19.0799L61.4128 19.1022Z"
        fill={light ? "#FFFFFF" : "#000000"}
      />
      <path
        d="M49.2588 13.7206C49.2588 11.4524 48.5491 9.71786 47.1519 8.53927C45.7768 7.36067 43.7585 6.76025 41.1193 6.76025C38.48 6.76025 36.8166 7.27172 35.375 8.27241C33.9334 9.27311 33.024 10.763 32.6248 12.7199H37.5041C37.9255 11.3412 39.1232 10.6518 41.1193 10.6518C43.1153 10.6518 44.557 11.6081 44.557 13.5205V14.2099H39.478C37.1936 14.2099 35.4415 14.6769 34.2217 15.6331C33.0019 16.5893 32.3809 17.9458 32.3809 19.7026C32.3809 21.4594 32.9797 22.7047 34.1552 23.7276C35.3306 24.7283 36.9719 25.2398 39.0566 25.2398C41.1414 25.2398 41.8733 24.9729 42.8936 24.417C43.9138 23.8388 44.7344 23.0382 45.3554 22.0153H46.5974V24.9284H51.5654V20.0584H49.2588V13.7206ZM44.5791 17.7234C44.5791 18.435 44.3795 19.0799 43.9581 19.6581C43.5367 20.214 42.9823 20.6588 42.2726 20.9924C41.5628 21.3259 40.7422 21.5038 39.8107 21.5038C38.8792 21.5038 38.3026 21.3259 37.7925 20.9701C37.3045 20.6143 37.0606 20.1029 37.0606 19.458C37.0606 18.1237 37.9477 17.4788 39.722 17.4788H44.5791V17.7457V17.7234Z"
        fill={light ? "#FFFFFF" : "#000000"}
      />
      <path
        d="M22.6666 0V4.84781H27.0802V4.98124L23.8864 17.0785H23.7755L19.5172 3.84712H10.4905V8.71716H11.7325V8.87283L9.22633 17.0785H9.07108L6.43182 7.11605H0V11.9861H2.7058V12.1195L6.32092 24.9062H11.3333L16.3013 11.1188H16.4122L21.6242 24.9062H26.6144L33.7116 0H22.6666Z"
        fill={light ? "#FFFFFF" : "#000000"}
      />
      <path
        d="M105.593 11.9861H106.835V12.1418L104.018 19.1022H103.907L98.939 7.11606H93.727L101.379 24.7505L99.9814 28.0639H96.0779V32H100.137C101.467 32 102.465 31.6664 103.131 31.0215C103.774 30.3989 104.351 29.4871 104.839 28.3308L106.28 24.8839L113.887 7.09382H105.593V11.9861Z"
        fill={light ? "#FFFFFF" : "#000000"}
      />
    </svg>
  );
}
